import React, { useState, useEffect, useMemo, useTransition } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useClientCandidates } from '../../hooks/useClientCandidates';
import { supabase } from '../../lib/supabaseClient';
import { Search, User, MapPin, Briefcase, Award, Loader2, AlertCircle, Copy, CheckCircle, Send, Filter, X, Check, Shield, Clock } from 'lucide-react';

// Feature flag: Set to true to enable messaging system, false to use email
const ENABLE_MESSAGING = false;

// Generate a 4-character hash from UUID for candidate display ID
const generateCandidateDisplayId = (uuid) => {
  if (!uuid) return 'XXXX';

  // Simple hash function to convert UUID to a short 4-char code
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to base36 and take first 4 characters, uppercase
  const displayId = Math.abs(hash).toString(36).toUpperCase().substring(0, 4).padStart(4, '0');
  return displayId;
};

const CLEARANCE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'Baseline', label: 'Baseline' },
  { value: 'NV1', label: 'NV1' },
  { value: 'NV2', label: 'NV2' },
  { value: 'TSPV', label: 'TSPV' },
];

const STATE_OPTIONS = [
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
];

const NOTICE_PERIOD_OPTIONS = [
  { value: 'Immediate', label: 'Immediate' },
  { value: '1 week', label: '1 week' },
  { value: '2 weeks', label: '2 weeks' },
  { value: '1 month', label: '1 month' },
  { value: '2 months', label: '2 months' },
  { value: '3 months', label: '3 months' },
];

const ClientCandidates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isPending, startTransition] = useTransition();

  // Multi-select filters
  const [selectedClearances, setSelectedClearances] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedNoticePeriods, setSelectedNoticePeriods] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);

  const [copiedId, setCopiedId] = useState(null);
  const [allSkills, setAllSkills] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [positionMappings, setPositionMappings] = useState({}); // Maps canonical position ID to array of mapping position IDs
  const [skillsSearchTerm, setSkillsSearchTerm] = useState('');
  const [debouncedSkillsSearchTerm, setDebouncedSkillsSearchTerm] = useState('');
  const [positionsSearchTerm, setPositionsSearchTerm] = useState('');
  const [debouncedPositionsSearchTerm, setDebouncedPositionsSearchTerm] = useState('');

  // Fetch all candidates (status: Active) - using secure hook that doesn't expose PII
  const { candidates: allCandidates, loading, error } = useClientCandidates({
    status: 'Active'
  });

  // Request dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(false);

  // Detail view dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailCandidate, setDetailCandidate] = useState(null);

  // Debounce main search term (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSearchTerm(searchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce skills search term (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSkillsSearchTerm(skillsSearchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [skillsSearchTerm]);

  // Debounce positions search term (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedPositionsSearchTerm(positionsSearchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [positionsSearchTerm]);

  // Fetch available skills
  useEffect(() => {
    const fetchSkills = async () => {
      const { data } = await supabase
        .from('skills_master')
        .select('id, name, category')
        .eq('status', 'active')
        .order('name');
      if (data) {
        setAllSkills(data);
      }
    };
    fetchSkills();
  }, []);

  // Fetch available positions and their mappings
  useEffect(() => {
    const fetchPositions = async () => {
      // Fetch active positions for dropdown
      const { data: activePositions } = await supabase
        .from('positions_master')
        .select('id, name, category')
        .eq('status', 'active')
        .order('name');

      if (activePositions) {
        setAllPositions(activePositions);

        // Fetch all mapping positions (positions that map TO active positions)
        const { data: mappingPositions } = await supabase
          .from('positions_master')
          .select('id, mapped_to_position_id')
          .eq('status', 'mapping')
          .not('mapped_to_position_id', 'is', null);

        if (mappingPositions) {
          // Create a map: canonical_position_id -> [mapping_position_id1, mapping_position_id2, ...]
          const mappings = {};
          mappingPositions.forEach(mp => {
            if (!mappings[mp.mapped_to_position_id]) {
              mappings[mp.mapped_to_position_id] = [];
            }
            mappings[mp.mapped_to_position_id].push(mp.id);
          });
          setPositionMappings(mappings);
        }
      }
    };
    fetchPositions();
  }, []);

  // Toggle filter selection
  const toggleFilter = (filterArray, setFilter, value) => {
    setFilter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  // Client-side filtering
  const filteredCandidates = useMemo(() => {
    return allCandidates.filter((candidate) => {
      // Search filter - searches in clearance, skills, and location
      if (debouncedSearchTerm) {
        const searchLower = debouncedSearchTerm.toLowerCase();

        // Search in clearance
        const clearance = (candidate.clearance_level || 'None').toLowerCase();
        const clearanceMatch = clearance.includes(searchLower);

        // Search in skills
        const candidateSkills = candidate.candidate_skills?.map(cs => cs.skill?.name?.toLowerCase()) || [];
        const skillsMatch = candidateSkills.some(skill => skill?.includes(searchLower));

        // Search in location (city and state)
        const locationCity = (candidate.location_city || '').toLowerCase();
        const locationState = (candidate.location_state || '').toLowerCase();
        const locationMatch = locationCity.includes(searchLower) || locationState.includes(searchLower);

        // If search doesn't match any of these fields, exclude candidate
        if (!clearanceMatch && !skillsMatch && !locationMatch) {
          return false;
        }
      }

      // Clearance filter
      if (selectedClearances.length > 0) {
        const clearance = candidate.clearance_level || 'None';
        if (!selectedClearances.includes(clearance)) {
          return false;
        }
      }

      // State filter
      if (selectedStates.length > 0 && !selectedStates.includes(candidate.location_state)) {
        return false;
      }

      // Notice period filter
      if (selectedNoticePeriods.length > 0 && !selectedNoticePeriods.includes(candidate.notice_period)) {
        return false;
      }

      // Skills filter - candidate must have at least one of the selected skills
      if (selectedSkills.length > 0) {
        const candidateSkillIds = candidate.candidate_skills?.map(cs => cs.skill?.id) || [];
        const hasMatchingSkill = selectedSkills.some(skillId => candidateSkillIds.includes(skillId));
        if (!hasMatchingSkill) {
          return false;
        }
      }

      // Position filter - includes mapped positions
      if (selectedPositions.length > 0) {
        // Build expanded list of position IDs (selected + their mappings)
        const expandedPositionIds = [];
        selectedPositions.forEach(positionId => {
          expandedPositionIds.push(positionId); // Add the canonical position
          if (positionMappings[positionId]) {
            expandedPositionIds.push(...positionMappings[positionId]); // Add mapped positions
          }
        });

        if (!expandedPositionIds.includes(candidate.position_id)) {
          return false;
        }
      }

      return true;
    });
  }, [allCandidates, selectedClearances, selectedStates, selectedNoticePeriods, selectedSkills, selectedPositions, debouncedSearchTerm, positionMappings]);

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedClearances([]);
    setSelectedStates([]);
    setSelectedNoticePeriods([]);
    setSelectedSkills([]);
    setSelectedPositions([]);
    setSearchTerm('');
  };

  // Count active filters
  const activeFiltersCount =
    selectedClearances.length +
    selectedStates.length +
    selectedNoticePeriods.length +
    selectedSkills.length +
    selectedPositions.length +
    (searchTerm ? 1 : 0);

  // Remove individual filter
  const removeFilter = (filterType, value) => {
    switch (filterType) {
      case 'clearance':
        setSelectedClearances((prev) => prev.filter((item) => item !== value));
        break;
      case 'state':
        setSelectedStates((prev) => prev.filter((item) => item !== value));
        break;
      case 'notice':
        setSelectedNoticePeriods((prev) => prev.filter((item) => item !== value));
        break;
      case 'skill':
        setSelectedSkills((prev) => prev.filter((item) => item !== value));
        break;
      case 'position':
        setSelectedPositions((prev) => prev.filter((item) => item !== value));
        break;
      case 'search':
        setSearchTerm('');
        break;
    }
  };

  // Filter skills list based on search term
  const filteredSkills = useMemo(() => {
    if (!debouncedSkillsSearchTerm) return allSkills;
    const searchLower = debouncedSkillsSearchTerm.toLowerCase();
    return allSkills.filter(skill =>
      skill.name.toLowerCase().includes(searchLower)
    );
  }, [allSkills, debouncedSkillsSearchTerm]);

  // Filter positions list based on search term
  const filteredPositions = useMemo(() => {
    if (!debouncedPositionsSearchTerm) return allPositions;
    const searchLower = debouncedPositionsSearchTerm.toLowerCase();
    return allPositions.filter(position =>
      position.name.toLowerCase().includes(searchLower)
    );
  }, [allPositions, debouncedPositionsSearchTerm]);

  const copyToClipboard = (displayId) => {
    navigator.clipboard.writeText(displayId).then(() => {
      setCopiedId(displayId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const openDetailDialog = (candidate) => {
    setDetailCandidate(candidate);
    setDetailDialogOpen(true);
  };

  const getStatusBadgeColor = (status) => {
    if (status === 'Active') {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const getClearanceBadgeColor = (clearance) => {
    const colors = {
      'TSPV': 'bg-purple-100 text-purple-700 border-purple-200',
      'NV2': 'bg-blue-100 text-blue-700 border-blue-200',
      'NV1': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'Baseline': 'bg-slate-100 text-slate-700 border-slate-200',
      'None': 'bg-slate-50 text-slate-500 border-slate-100',
    };
    return colors[clearance] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const openRequestDialog = (candidate) => {
    setSelectedCandidate(candidate);
    setRequestMessage('');
    setRequestError(null);
    setRequestSuccess(false);
    setRequestDialogOpen(true);
  };

  const closeRequestDialog = () => {
    setRequestDialogOpen(false);
    setSelectedCandidate(null);
    setRequestMessage('');
    setRequestError(null);
  };

  const submitRequest = async () => {
    if (!selectedCandidate || !requestMessage.trim()) {
      setRequestError('Please enter a message');
      return;
    }

    setSubmitting(true);
    setRequestError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get client_id from client_contacts
      const { data: contact, error: contactError } = await supabase
        .from('client_contacts')
        .select('client_id')
        .eq('user_id', user.id)
        .single();

      if (contactError) throw contactError;

      if (!contact?.client_id) {
        throw new Error('Client information not found');
      }

      // Insert candidate request
      const { error: insertError } = await supabase
        .from('candidate_requests')
        .insert({
          candidate_id: selectedCandidate.id,
          client_id: contact.client_id,
          requested_by_user_id: user.id,
          message: requestMessage.trim(),
          status: 'pending',
        });

      if (insertError) throw insertError;

      setRequestSuccess(true);
      setTimeout(() => {
        closeRequestDialog();
      }, 2000);
    } catch (err) {
      console.error('Error submitting request:', err);
      setRequestError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Candidates</h2>
        <p className="text-muted-foreground mt-1">Browse available candidates</p>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader className="px-6 py-4">
          <div className="flex flex-col gap-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {/* Search */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search clearance, skills, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>

              {/* Filter Dropdowns */}
              <div className="flex flex-wrap gap-2">
                {/* Clearance Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Filter className="mr-2 h-4 w-4" />
                      Clearance
                      {selectedClearances.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedClearances.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <div className="p-2 space-y-1">
                      {CLEARANCE_OPTIONS.map((option) => (
                        <div
                          key={option.value}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => toggleFilter(selectedClearances, setSelectedClearances, option.value)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedClearances.includes(option.value) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <span className="text-sm">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* State Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <MapPin className="mr-2 h-4 w-4" />
                      State
                      {selectedStates.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedStates.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <div className="p-2 space-y-1">
                      {STATE_OPTIONS.map((option) => (
                        <div
                          key={option.value}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => toggleFilter(selectedStates, setSelectedStates, option.value)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedStates.includes(option.value) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <span className="text-sm">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Availability Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Briefcase className="mr-2 h-4 w-4" />
                      Availability
                      {selectedNoticePeriods.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedNoticePeriods.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <div className="p-2 space-y-1">
                      {NOTICE_PERIOD_OPTIONS.map((option) => (
                        <div
                          key={option.value}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => toggleFilter(selectedNoticePeriods, setSelectedNoticePeriods, option.value)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedNoticePeriods.includes(option.value) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <span className="text-sm">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Skills Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Award className="mr-2 h-4 w-4" />
                      Skills
                      {selectedSkills.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedSkills.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="start">
                    <div className="p-2">
                      <Input
                        placeholder="Search skills..."
                        value={skillsSearchTerm}
                        onChange={(e) => setSkillsSearchTerm(e.target.value)}
                        className="mb-2 h-8"
                      />
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {filteredSkills.map((skill) => (
                          <div
                            key={skill.id}
                            className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                            onClick={() => toggleFilter(selectedSkills, setSelectedSkills, skill.id)}
                          >
                            <div className="flex items-center justify-center w-4 h-4 border rounded">
                              {selectedSkills.includes(skill.id) && (
                                <Check className="h-3 w-3" />
                              )}
                            </div>
                            <span className="text-sm">{skill.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Position Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 border-dashed">
                      <Briefcase className="mr-2 h-4 w-4" />
                      Position
                      {selectedPositions.length > 0 && (
                        <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                          {selectedPositions.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="start">
                    <div className="p-2">
                      <Input
                        placeholder="Search positions..."
                        value={positionsSearchTerm}
                        onChange={(e) => setPositionsSearchTerm(e.target.value)}
                        className="mb-2 h-8"
                      />
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {filteredPositions.map((position) => (
                          <div
                            key={position.id}
                            className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                            onClick={() => toggleFilter(selectedPositions, setSelectedPositions, position.id)}
                          >
                            <div className="flex items-center justify-center w-4 h-4 border rounded">
                              {selectedPositions.includes(position.id) && (
                                <Check className="h-3 w-3" />
                              )}
                            </div>
                            <span className="text-sm">{position.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Clear Filters Button */}
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9"
                    onClick={clearAllFilters}
                  >
                    Clear
                    <X className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Active Filters Display */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2">
                {searchTerm && (
                  <Badge variant="secondary" className="gap-1">
                    Search: {searchTerm}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-foreground"
                      onClick={() => removeFilter('search')}
                    />
                  </Badge>
                )}
                {selectedClearances.map((clearance) => (
                  <Badge key={clearance} variant="secondary" className="gap-1">
                    Clearance: {clearance}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-foreground"
                      onClick={() => removeFilter('clearance', clearance)}
                    />
                  </Badge>
                ))}
                {selectedStates.map((state) => (
                  <Badge key={state} variant="secondary" className="gap-1">
                    State: {state}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-foreground"
                      onClick={() => removeFilter('state', state)}
                    />
                  </Badge>
                ))}
                {selectedNoticePeriods.map((period) => (
                  <Badge key={period} variant="secondary" className="gap-1">
                    Availability: {period}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-foreground"
                      onClick={() => removeFilter('notice', period)}
                    />
                  </Badge>
                ))}
                {selectedSkills.map((skillId) => {
                  const skill = allSkills.find(s => s.id === skillId);
                  return skill ? (
                    <Badge key={skillId} variant="secondary" className="gap-1">
                      Skill: {skill.name}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-foreground"
                        onClick={() => removeFilter('skill', skillId)}
                      />
                    </Badge>
                  ) : null;
                })}
                {selectedPositions.map((positionId) => {
                  const position = allPositions.find(p => p.id === positionId);
                  return position ? (
                    <Badge key={positionId} variant="secondary" className="gap-1">
                      Position: {position.name}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-foreground"
                        onClick={() => removeFilter('position', positionId)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Candidates List */}
      {!loading && !error && (
        <>
          {filteredCandidates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium">No candidates found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {activeFiltersCount > 0
                    ? 'Try adjusting your filters'
                    : 'There are no active candidates at the moment'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Showing {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''}
              </p>

              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                {filteredCandidates.map((candidate) => {
                  const displayId = `Candidate-${generateCandidateDisplayId(candidate.id)}`;
                  const isCopied = copiedId === displayId;
                  const skills = candidate.candidate_skills || [];
                  const displaySkills = skills.slice(0, 5);
                  const remainingSkills = skills.length - 5;

                  return (
                    <Card
                      key={candidate.id}
                      className="hover:shadow-md transition-shadow cursor-pointer h-full"
                      onClick={() => openDetailDialog(candidate)}
                    >
                      <CardContent className="p-4 flex flex-col h-full">
                        {/* Header: Reference + Status */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{displayId}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(displayId);
                              }}
                              className="text-muted-foreground hover:text-foreground transition-colors"
                              title="Copy candidate reference"
                            >
                              {isCopied ? (
                                <CheckCircle className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                          <Badge variant="outline" className={getStatusBadgeColor(candidate.status)}>
                            {candidate.status}
                          </Badge>
                        </div>

                        {/* Current Position */}
                        {candidate.position && (
                          <div className="flex items-center gap-2 mb-3">
                            <Briefcase className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">Current position:</span>
                            <span className="text-xs font-medium">{candidate.position.name}</span>
                          </div>
                        )}

                        {/* Key Facts */}
                        <div className="space-y-2 text-sm mb-3 flex-grow">
                          {/* Clearance */}
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground text-xs">Clearance:</span>
                            <Badge variant="outline" className={`${getClearanceBadgeColor(candidate.clearance_level)} text-xs`}>
                              {candidate.clearance_level || 'None'}
                            </Badge>
                          </div>

                          {/* Skills - show 5 max */}
                          <div className="flex items-start gap-2">
                            <Award className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <span className="text-muted-foreground text-xs">Skills:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {skills.length > 0 ? (
                                  <>
                                    {displaySkills.map((cs) => (
                                      <Badge key={cs.id} variant="secondary" className="text-xs">
                                        {cs.skill?.name}
                                      </Badge>
                                    ))}
                                    {remainingSkills > 0 && (
                                      <Badge variant="secondary" className="text-xs">
                                        +{remainingSkills}
                                      </Badge>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-muted-foreground">No skills listed</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Location */}
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground text-xs">Location:</span>
                            <span className="text-xs truncate">
                              {candidate.location_city && candidate.location_state
                                ? `${candidate.location_city}, ${candidate.location_state}`
                                : candidate.location_city || candidate.location_state || 'Not specified'}
                            </span>
                          </div>

                          {/* Notice Period */}
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground text-xs">Notice:</span>
                            <span className="text-xs">{candidate.notice_period || 'Not specified'}</span>
                          </div>
                        </div>

                        {/* More Details Button - size="sm" */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full h-8 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailDialog(candidate);
                          }}
                        >
                          <User className="h-3 w-3 mr-1" />
                          More Details
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* Request Details Dialog - Only show if messaging is enabled */}
      {ENABLE_MESSAGING && (
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-0">
          {/* Header - sticky at top */}
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>Request Candidate Details</DialogTitle>
              <DialogDescription>
                {selectedCandidate && (
                  <span>
                    Send a message to your account manager about{' '}
                    <strong>Candidate-{generateCandidateDisplayId(selectedCandidate.id)}</strong>.
                    You can continue the conversation in the <strong>Messages</strong> tab.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {requestSuccess ? (
              <Alert className="bg-green-50 text-green-900 border-green-200">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Your message has been sent to your account manager! Check the <strong>Messages</strong> tab to continue the conversation.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {requestError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{requestError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="request-message">Message to Account Manager *</Label>
                  <Textarea
                    id="request-message"
                    placeholder="Please provide details about what information you need (e.g., full resume, availability for interview, rate expectations, etc.)"
                    value={requestMessage}
                    onChange={(e) => setRequestMessage(e.target.value)}
                    rows={5}
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    This will send a message to your account manager. Continue the conversation in the Messages tab.
                  </p>
                </div>

                {selectedCandidate && (
                  <div className="rounded-lg bg-muted p-3 space-y-1 text-sm">
                    <p className="font-medium">Candidate Summary:</p>
                    <p className="text-muted-foreground">
                      Location: {[selectedCandidate.location_city, selectedCandidate.location_state]
                        .filter(Boolean)
                        .join(', ') || 'Not specified'}
                    </p>
                    {selectedCandidate.clearance_level && (
                      <p className="text-muted-foreground">
                        Clearance: {selectedCandidate.clearance_level}
                      </p>
                    )}
                    {selectedCandidate.notice_period && (
                      <p className="text-muted-foreground">
                        Notice Period: {selectedCandidate.notice_period}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer - sticky at bottom */}
          <div className="border-t p-6 bg-background">
            <DialogFooter>
              {!requestSuccess && (
                <>
                  <Button
                    variant="outline"
                    onClick={closeRequestDialog}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitRequest}
                    disabled={submitting || !requestMessage.trim()}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Request
                      </>
                    )}
                  </Button>
                </>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      )}

      {/* Candidate Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          {detailCandidate && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Candidate-{generateCandidateDisplayId(detailCandidate.id)}
                </DialogTitle>
                <DialogDescription>
                  Complete candidate profile
                </DialogDescription>
              </DialogHeader>

              {/* Contact Information Alert */}
              <Alert className="bg-blue-50 border-blue-200 mt-4">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-sm text-blue-900">
                  <p className="font-medium mb-1">To request additional candidate information:</p>
                  <p className="mb-2">
                    Email us at <a href="mailto:contact@appsytec.com" className="font-semibold underline">contact@appsytec.com</a> quoting{' '}
                    <span className="font-semibold">Candidate-{generateCandidateDisplayId(detailCandidate.id)}</span> and the nature of your request.
                  </p>
                  <p className="text-xs">
                    We encourage email correspondence to maintain an audit trail and ensure transparency in accordance with procurement policies.
                    We will facilitate contact with the candidate through appropriate channels.
                  </p>
                </AlertDescription>
              </Alert>

              <div className="space-y-6 mt-4">
                {/* Status */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Status</p>
                  <Badge variant="outline" className={getStatusBadgeColor(detailCandidate.status)}>
                    {detailCandidate.status}
                  </Badge>
                </div>

                {/* Security Clearance */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">Security Clearance</p>
                  </div>
                  <Badge variant="outline" className={`${getClearanceBadgeColor(detailCandidate.clearance_level)}`}>
                    {detailCandidate.clearance_level || 'None'}
                  </Badge>
                </div>

                {/* Location */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">Location</p>
                  </div>
                  <p className="text-sm">
                    {detailCandidate.location_city && detailCandidate.location_state
                      ? `${detailCandidate.location_city}, ${detailCandidate.location_state}`
                      : detailCandidate.location_city || detailCandidate.location_state || 'Not specified'}
                  </p>
                </div>

                {/* Notice Period */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">Notice Period</p>
                  </div>
                  <p className="text-sm">{detailCandidate.notice_period || 'Not specified'}</p>
                </div>

                {/* Citizenship */}
                {detailCandidate.citizenship_status && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Citizenship Status</p>
                    <p className="text-sm">{detailCandidate.citizenship_status}</p>
                  </div>
                )}

                {/* Skills - Show all */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">
                      Skills ({detailCandidate.candidate_skills?.length || 0})
                    </p>
                  </div>
                  {detailCandidate.candidate_skills && detailCandidate.candidate_skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {detailCandidate.candidate_skills.map((cs) => (
                        <Badge key={cs.id} variant="secondary">
                          {cs.skill?.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No skills listed</p>
                  )}
                </div>
              </div>

              <DialogFooter className="mt-6">
                {ENABLE_MESSAGING ? (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailDialogOpen(false);
                      openRequestDialog(detailCandidate);
                    }}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Email Us
                  </Button>
                ) : (
                  <Button asChild>
                    <a
                      href={`mailto:contact@appsytec.com?subject=Candidate Inquiry: Candidate-${generateCandidateDisplayId(detailCandidate.id)}&body=Dear Appsytec Team,%0A%0AI am interested in learning more about Candidate-${generateCandidateDisplayId(detailCandidate.id)}.%0A%0APlease provide the following information:%0A- [ ] Full resume%0A- [ ] Availability for interview%0A- [ ] Rate expectations%0A- [ ] Other (please specify below)%0A%0AAdditional details about my request:%0A%0A%0A%0AThank you for facilitating contact through appropriate channels and maintaining an audit trail for our procurement records.%0A%0ABest regards`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Email Us
                    </a>
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCandidates;
